import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Columns3, RotateCcw, Check, Minus, GripVertical, Info, ChevronRight } from 'lucide-react';
import './ReportsNative.css';

/**
 * Column customization control for ReportMatrix / ReportDetailTable.
 * Driven by the `columnPrefs` object from pages/reports/useColumnPrefs:
 * { allColumns, visibleKeys, isCustomized, hasSavedDefault,
 *   toggleColumn, resetToDefault, saveAsDefault, clearSavedDefault }.
 *
 * Renders a "Columns" picker popover plus — whenever the active set differs
 * from the user's default — an orange-hued "Customized · Reset" pill that
 * restores the default in one click.
 *
 * `sections` adds extra checkbox groups above the columns list for choices
 * that aren't columns but belong in the same control — e.g. the P&L's
 * Location row tier, which unchecks into a consolidated statement:
 *   [{ title, items: [{ key, label, description?, checked, onToggle }] }]
 *
 * Grouped columns: a column with `picker: { section, group, label }` leaves
 * the flat list and renders under a `section` subheading (after the plain
 * columns, same scroll) as a tree: one row per `group` — tri-state box (all
 * on / off / mixed) + name + chevron — that expands to its columns as normal
 * checkbox rows (`label`, e.g. Rooms · ADR) indented one level. Collapsed by
 * default, mixed groups open; expansion is component state only. Child rows
 * toggle the same column keys as flat rows, so URL/saved sets are unchanged.
 */
export function ColumnPickerControl({ columnPrefs, sections }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [dragOver, setDragOver] = useState(null); // { key, after }
  const [tip, setTip] = useState(null); // { text, x, y } — fixed-position metric explanation
  const [pos, setPos] = useState(null); // fixed-position anchor for the portaled popover
  const [groupOpen, setGroupOpen] = useState({}); // group title → expanded (explicit user choice)
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTip(null);
      return undefined;
    }
    const onDown = (e) => {
      // the popover portals to <body>, so check both the trigger and the pop
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // The popover renders in a <body> portal at position:fixed so nothing —
  // card overflow, table scroll wrappers — can clip it. It sizes to the
  // VIEWPORT: max-height is whatever screen space remains, and it flips
  // upward when the trigger sits near the bottom (e.g. the floating dock).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const GAP = 6;
    const MARGIN = 12;
    const compute = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const spaceBelow = vh - rect.bottom - GAP - MARGIN;
      const spaceAbove = rect.top - GAP - MARGIN;
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow;
      setPos({
        right: Math.max(MARGIN, vw - rect.right),
        top: openUp ? undefined : rect.bottom + GAP,
        bottom: openUp ? vh - rect.top + GAP : undefined,
        maxHeight: Math.max(180, openUp ? spaceAbove : spaceBelow),
      });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open]);

  if (!columnPrefs) return null;
  const {
    allColumns,
    visibleKeys,
    isCustomized,
    hasSavedDefault,
    toggleColumn,
    reorderColumns,
    facets,
    resetToDefault,
    saveAsDefault,
    clearSavedDefault,
  } = columnPrefs;

  // Faceted tables (e.g. the P&L Income Statement) collapse the picker to one
  // control per metric — Actual / Budget / Var — that applies across every
  // month group + Total, instead of a row per (group × metric). Flat tables
  // keep the per-column list.
  const faceted = !!facets;

  // Normalize both modes to a single { key, label, checked } item list plus the
  // reorderable "visible sequence" and its toggle/reorder handlers.
  const byKey = new Map(allColumns.map((c) => [c.key, c]));
  const pickerSections = faceted ? [] : groupPickerColumns(allColumns);
  const isFlat = (c) => faceted || !c?.picker?.group;
  const pickerItems = faceted
    ? facets.items
    : [
        ...visibleKeys.map((k) => byKey.get(k)).filter(Boolean),
        ...allColumns.filter((c) => !visibleKeys.includes(c.key)),
      ].filter(isFlat).map((c) => ({
        key: c.key,
        label: c.group ? `${c.group} · ${c.label}` : c.label,
        description: c.description || null,
        checked: visibleKeys.includes(c.key),
      }));
  const visibleSeq = faceted ? facets.visibleFacets : visibleKeys;
  const onToggleItem = faceted ? facets.toggleFacet : toggleColumn;
  const onReorderVisible = faceted ? facets.reorderFacets : reorderColumns;

  // Group "all" box: show every column of the group, or (all on) hide them —
  // one sequence write so the keys land at their built-in positions together.
  const toggleGroup = (keys) => {
    const allOn = keys.every((k) => visibleKeys.includes(k));
    const next = setKeysVisible(visibleKeys, allColumns.map((c) => c.key), keys, !allOn);
    if (next !== visibleKeys) reorderColumns(next);
  };

  // ⓘ with the column's explanation (fixed-position tooltip, like the flat rows)
  const infoTip = (text, ariaLabel) => (
    <span
      className="rn-colinfo"
      aria-label={ariaLabel}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTip({ text, x: rect.left, y: rect.bottom + 6 });
      }}
      onMouseLeave={() => setTip(null)}
      onClick={(e) => e.preventDefault()}
    >
      <Info />
    </span>
  );

  const handleSave = async (fn) => {
    setSaving(true);
    try {
      await fn();
    } catch (e) {
      // surfaced via console; picker stays open so the user can retry
      console.error('Failed to update column preferences:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="rn-colpicker" ref={wrapRef}>
      {isCustomized && (
        <button
          type="button"
          className="rn-customized-pill"
          title="Columns differ from your default — click to reset"
          onClick={resetToDefault}
        >
          <RotateCcw />
          Customized · Reset
        </button>
      )}
      <button
        type="button"
        className={`rn-icon-btn${open ? ' rn-icon-btn-active' : ''}`}
        title="Choose columns"
        onClick={() => setOpen((v) => !v)}
      >
        <Columns3 />
      </button>
      {open && pos && createPortal(
        <div
          className="rn-colpicker-pop rn-colpicker-pop-fixed"
          role="menu"
          ref={popRef}
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            right: pos.right,
            maxHeight: pos.maxHeight,
          }}
        >
          {(sections || []).map((section) => (
            <React.Fragment key={section.title}>
              <div className="rn-colpicker-head">{section.title}</div>
              <div className="rn-colpicker-list">
                {section.items.map((item) => (
                  <div key={item.key} className="rn-colpicker-item">
                    <GripVertical className="rn-colpicker-grip rn-colpicker-grip-off" aria-hidden="true" style={{ visibility: 'hidden' }} />
                    <label className="rn-colpicker-label">
                      <span className={`rn-colpicker-box${item.checked ? ' rn-colpicker-box-on' : ''}`}>
                        {item.checked && <Check />}
                      </span>
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => item.onToggle(!item.checked)}
                        style={{ display: 'none' }}
                      />
                      {item.label}
                    </label>
                    {item.description && (
                      <span
                        className="rn-colinfo"
                        aria-label={`What ${item.label} does`}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTip({ text: item.description, x: rect.left, y: rect.bottom + 6 });
                        }}
                        onMouseLeave={() => setTip(null)}
                        onClick={(e) => e.preventDefault()}
                      >
                        <Info />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
          <div className="rn-colpicker-head">Columns</div>
          <div className="rn-colpicker-list">
            {pickerItems.map((item) => {
              const checked = item.checked;
              const isDragSource = dragKey === item.key;
              const dropHere = dragOver?.key === item.key && dragKey && dragKey !== item.key;
              return (
                <div
                  key={item.key}
                  className={[
                    'rn-colpicker-item',
                    checked ? 'rn-colpicker-item-draggable' : '',
                    isDragSource ? 'rn-colpicker-item-dragging' : '',
                    dropHere ? (dragOver.after ? 'rn-drop-after' : 'rn-drop-before') : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  draggable={checked}
                  onDragStart={checked ? (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', item.key); } catch (_) { /* IE */ }
                    setDragKey(item.key);
                  } : undefined}
                  onDragOver={checked ? (e) => {
                    if (!dragKey || dragKey === item.key) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const rect = e.currentTarget.getBoundingClientRect();
                    const after = e.clientY > rect.top + rect.height / 2;
                    setDragOver((cur) => (cur?.key === item.key && cur.after === after ? cur : { key: item.key, after }));
                  } : undefined}
                  onDrop={checked ? (e) => {
                    e.preventDefault();
                    if (dragKey && dragKey !== item.key) {
                      const next = visibleSeq.filter((k) => k !== dragKey);
                      const idx = next.indexOf(item.key) + (dragOver?.after ? 1 : 0);
                      next.splice(idx, 0, dragKey);
                      onReorderVisible(next);
                    }
                    setDragKey(null);
                    setDragOver(null);
                  } : undefined}
                  onDragEnd={() => {
                    setDragKey(null);
                    setDragOver(null);
                  }}
                >
                  <GripVertical className={`rn-colpicker-grip${checked ? '' : ' rn-colpicker-grip-off'}`} aria-hidden="true" />
                  <label className="rn-colpicker-label">
                    <span className={`rn-colpicker-box${checked ? ' rn-colpicker-box-on' : ''}`}>
                      {checked && <Check />}
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleItem(item.key)}
                      style={{ display: 'none' }}
                    />
                    {item.label}
                  </label>
                  {item.description && (
                    <span
                      className="rn-colinfo"
                      aria-label={`How ${item.label} is calculated`}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTip({ text: item.description, x: rect.left, y: rect.bottom + 6 });
                      }}
                      onMouseLeave={() => setTip(null)}
                      onClick={(e) => e.preventDefault()}
                    >
                      <Info />
                    </span>
                  )}
                </div>
              );
            })}
            {pickerSections.map((section) => (
              <div key={section.title} className="rn-colpicker-groups" role="group" aria-label={section.title}>
                {section.title && <div className="rn-colpicker-subhead">{section.title}</div>}
                {section.groups.map((group) => {
                  const keys = group.columns.map((c) => c.key);
                  const onCount = keys.filter((k) => visibleKeys.includes(k)).length;
                  const allOn = onCount === keys.length;
                  const some = onCount > 0 && !allOn;
                  const expanded = groupOpen[group.title] ?? some;
                  return (
                    <div key={group.title} className="rn-colpicker-tree" role="group" aria-label={group.title}>
                      <div className="rn-colpicker-item">
                        <GripVertical className="rn-colpicker-grip rn-colpicker-grip-off" aria-hidden="true" />
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={allOn ? 'true' : some ? 'mixed' : 'false'}
                          className="rn-colpicker-label rn-colpicker-btn"
                          onClick={() => toggleGroup(keys)}
                        >
                          <span className={`rn-colpicker-box${onCount > 0 ? ' rn-colpicker-box-on' : ''}`}>
                            {allOn && <Check />}
                            {some && <Minus />}
                          </span>
                          <span className="rn-colpicker-text">{group.title}</span>
                        </button>
                        <button
                          type="button"
                          className={`rn-colpicker-caret${expanded ? ' rn-open' : ''}`}
                          aria-expanded={expanded}
                          aria-label={`${group.title} options`}
                          onClick={() => setGroupOpen((cur) => ({ ...cur, [group.title]: !expanded }))}
                        >
                          <ChevronRight />
                        </button>
                      </div>
                      {expanded && group.columns.map((c) => {
                        const on = visibleKeys.includes(c.key);
                        const label = c.picker.label || c.label;
                        return (
                          <div key={c.key} className="rn-colpicker-item rn-colpicker-item-child">
                            <GripVertical className="rn-colpicker-grip rn-colpicker-grip-off" aria-hidden="true" />
                            <button
                              type="button"
                              role="checkbox"
                              aria-checked={on}
                              aria-label={`${group.title} ${label}`}
                              className="rn-colpicker-label rn-colpicker-btn"
                              onClick={() => toggleColumn(c.key)}
                            >
                              <span className={`rn-colpicker-box${on ? ' rn-colpicker-box-on' : ''}`}>
                                {on && <Check />}
                              </span>
                              <span className="rn-colpicker-text">{label}</span>
                            </button>
                            {c.description && infoTip(c.description, `How ${group.title} ${label} is calculated`)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {tip && (
            <div className="rn-coltip" style={{ left: Math.max(8, tip.x - 220), top: tip.y }} role="tooltip">
              {tip.text}
            </div>
          )}
          <div className="rn-colpicker-foot">
            <button
              type="button"
              className="rn-btn rn-btn-xs"
              disabled={saving || !isCustomized}
              onClick={() => handleSave(saveAsDefault)}
            >
              {saving ? 'Saving…' : 'Save as my default'}
            </button>
            <button type="button" className="rn-btn rn-btn-xs" disabled={!isCustomized} onClick={resetToDefault}>
              Reset
            </button>
            {hasSavedDefault && (
              <button
                type="button"
                className="rn-btn rn-btn-xs rn-btn-quiet"
                disabled={saving}
                onClick={() => handleSave(clearSavedDefault)}
                title="Remove your saved default (back to all columns)"
              >
                Clear saved
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

/**
 * Columns with `picker.group` → [{ title: section, groups: [{ title, columns }] }]
 * in first-appearance order (kit-internal; exported for tests).
 */
export function groupPickerColumns(columns) {
  const sections = [];
  (columns || []).forEach((c) => {
    const p = c?.picker;
    if (!p?.group) return;
    const title = p.section || '';
    let section = sections.find((s) => s.title === title);
    if (!section) {
      section = { title, groups: [] };
      sections.push(section);
    }
    let group = section.groups.find((g) => g.title === p.group);
    if (!group) {
      group = { title: p.group, columns: [] };
      section.groups.push(group);
    }
    group.columns.push(c);
  });
  return sections;
}

/**
 * Show (on) or hide several keys in one visible sequence. Shown keys go in at
 * their built-in (`allKeys`) position like useColumnPrefs' toggleColumn;
 * hiding never empties the table. Returns `visibleKeys` itself when nothing
 * changes.
 */
export function setKeysVisible(visibleKeys, allKeys, keys, on) {
  if (!on) {
    const next = visibleKeys.filter((k) => !keys.includes(k));
    return next.length === visibleKeys.length || next.length === 0 ? visibleKeys : next;
  }
  const missing = keys.filter((k) => !visibleKeys.includes(k));
  if (missing.length === 0) return visibleKeys;
  const next = [...visibleKeys];
  missing.forEach((key) => {
    const builtInIdx = allKeys.indexOf(key);
    const at = next.findIndex((k) => allKeys.indexOf(k) > builtInIdx);
    next.splice(at === -1 ? next.length : at, 0, key);
  });
  return next;
}

/**
 * Filter AND order a columns config by columnPrefs.visibleKeys
 * (kit-internal) — the visibleKeys sequence is the render order.
 */
export function applyColumnPrefs(columns, columnPrefs) {
  if (!columnPrefs || !Array.isArray(columns)) return columns;
  const { visibleKeys } = columnPrefs;
  if (!Array.isArray(visibleKeys) || visibleKeys.length === 0) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const ordered = visibleKeys.map((k) => byKey.get(k)).filter(Boolean);
  return ordered.length > 0 ? ordered : columns;
}

export default ColumnPickerControl;
